/* eslint-disable sonarjs/no-duplicate-string */

import { initIosPageScrollLock } from './initIosPageScrollLock';
import { getDevicePlatform, isNativeWebView } from './nativeWebView';

const KEYBOARD_OPEN_THRESHOLD = 80;

const isEditableElement = (element: Element | null): boolean => {
  if (!element || !(element instanceof HTMLElement)) {
    return false;
  }

  if (element.isContentEditable) {
    return true;
  }

  const tagName = element.tagName;

  return tagName === 'INPUT' || tagName === 'TEXTAREA';
};

export const liveVh = () => {
  // Приложение определяем по маркеру, платформу — по UA; атрибуты выставляем
  // только в приложении, чтобы нативные стили не включались в мобильном браузере.
  const isNativeApp = isNativeWebView();
  const devicePlatform = getDevicePlatform();
  const isNativeIosWebView = isNativeApp && devicePlatform === 'ios';

  if (isNativeApp) {
    document.documentElement.setAttribute('data-native-webview', '');
    document.documentElement.setAttribute('data-native-platform', devicePlatform ?? '');
  }

  if (isNativeIosWebView) {
    initIosPageScrollLock();
  }

  // Запоминаем высоту viewport без клавиатуры (обновляется на resize окна)
  let initialHeight = window.innerHeight;
  let isKeyboardOpen = false;

  const update = () => {
    const vh = window.innerHeight * 0.01;

    document.documentElement.style.setProperty('--live-vh', `${vh}px`);

    const vw = window.innerWidth * 0.01;

    document.documentElement.style.setProperty('--live-vw', `${vw}px`);

    // Обновляем initial height только если это не появление клавиатуры
    // (при появлении клавиатуры на Android innerHeight уменьшается)
    const vv = window.visualViewport;
    const keyboardHeight = vv ? initialHeight - vv.height - vv.offsetTop : 0;
    const isKeyboardResize = isEditableElement(document.activeElement) && keyboardHeight > KEYBOARD_OPEN_THRESHOLD;

    if (!vv || (!isKeyboardResize && Math.abs(vv.height - window.innerHeight) < 100)) {
      initialHeight = window.innerHeight;
    }
  };

  // Обновляет --keyboard-offset: расстояние от нижнего края layout viewport
  // до верха клавиатуры. Используется для позиционирования fixed элементов.
  // --keyboard-overlay-offset нужен для экранов, высота которых уже завязана на
  // --live-vh: на Android viewport часто уже уменьшен клавиатурой, а на iOS нет.
  //
  // Особенности платформ:
  // - iOS Safari: window.innerHeight не меняется, visualViewport.height уменьшается
  // - Android Chrome: оба значения могут меняться, используем сохранённую initialHeight
  // - Desktop: visualViewport === window размеры, offset = 0
  const updateKeyboardOffset = () => {
    const vv = window.visualViewport;

    if (!vv) {
      isKeyboardOpen = false;
      document.documentElement.style.setProperty('--keyboard-offset', '0px');
      document.documentElement.style.setProperty('--keyboard-overlay-offset', '0px');
      document.documentElement.removeAttribute('data-keyboard-open');

      return;
    }

    // На iOS используем разницу с innerHeight (он не меняется при клавиатуре)
    // На Android innerHeight меняется, поэтому сравниваем с сохранённой initialHeight
    const referenceHeight = Math.max(window.innerHeight, initialHeight);
    const keyboardHeight = referenceHeight - vv.height - vv.offsetTop;
    const layoutViewportShrink = Math.max(0, initialHeight - window.innerHeight);
    const keyboardOverlayHeight = Math.max(0, keyboardHeight - layoutViewportShrink);

    isKeyboardOpen =
      isEditableElement(document.activeElement) && (isNativeIosWebView || keyboardHeight > KEYBOARD_OPEN_THRESHOLD);

    document.documentElement.style.setProperty('--keyboard-offset', `${Math.max(0, keyboardHeight)}px`);
    document.documentElement.style.setProperty('--keyboard-overlay-offset', `${keyboardOverlayHeight}px`);
    document.documentElement.toggleAttribute('data-keyboard-open', isKeyboardOpen);
  };

  const updateViewportTopOffset = () => {
    const viewport = window.visualViewport;
    const topOffset = isKeyboardOpen ? (viewport?.offsetTop ?? 0) : 0;
    const visualVh = (viewport?.height ?? window.innerHeight) * 0.01;
    const visualVw = (viewport?.width ?? window.innerWidth) * 0.01;

    document.documentElement.style.setProperty('--viewport-top-offset', `${Math.max(0, topOffset)}px`);
    document.documentElement.style.setProperty('--visual-vh', `${visualVh}px`);
    document.documentElement.style.setProperty('--visual-vw', `${visualVw}px`);
  };

  const updateViewportMetrics = () => {
    updateKeyboardOffset();
    updateViewportTopOffset();
  };

  const vh = window.innerHeight * 0.01;

  document.documentElement.style.setProperty('--initial-vh', `${vh}px`);

  const vw = window.innerWidth * 0.01;

  document.documentElement.style.setProperty('--initial-vw', `${vw}px`);
  document.documentElement.style.setProperty('--visual-vh', `${vh}px`);
  document.documentElement.style.setProperty('--visual-vw', `${vw}px`);

  document.documentElement.style.setProperty('--keyboard-offset', '0px');
  document.documentElement.style.setProperty('--keyboard-overlay-offset', '0px');
  document.documentElement.style.setProperty('--viewport-top-offset', '0px');

  window.addEventListener('resize', update);
  window.addEventListener('resize', updateViewportMetrics);
  window.addEventListener('orientationchange', updateViewportMetrics);
  window.visualViewport?.addEventListener('resize', updateViewportMetrics);
  window.visualViewport?.addEventListener('scroll', updateViewportMetrics);
  document.addEventListener('focusin', updateViewportMetrics);
  document.addEventListener('focusout', updateViewportMetrics);

  update();
  updateViewportMetrics();
};
